/**
 * Sticky Block Builder Alpine.js Component
 *
 * Component specifically for sticky/transparent header functionality:
 * - Transparent header logic with page-specific targeting
 * - Sticky positioning modes (never, always, scroll-up)
 * - Header inversion on scroll
 * - Background changes on scroll
 * - All blockBuilder functionality for reparenting and events
 */

// Global function to create stickyBlockBuilder with settings from store
window.stickyBlockBuilderSettings = function(id, type = 'section', colorSchema = '', additionalData = '') {
  // Ensure settings store is available
  if (!window.arcthemeSettingsStore) {
    console.warn('Arctheme Settings Store is not available. Falling back to empty settings.');
    return stickyBlockBuilder.component({}, colorSchema, type);
  }

  // Get settings from the global store
  const settings = window.arcthemeSettingsStore.get(type, id) || {};
  
  // Debug log to verify settings are loaded
  if (window.enableDebugMode) { console.log(`StickyBlockBuilder loading settings for ${type}:${id}:`, settings); }

  // Parse additional data if provided
  let parsedAdditionalData = {};
  if (additionalData) {
    try {
      parsedAdditionalData = JSON.parse(additionalData);
    } catch (e) {
      console.warn('Failed to parse additional data for stickyBlockBuilder:', e);
    }
  }

  // Create component instance
  const componentInstance = {
    ...stickyBlockBuilder.component(settings, colorSchema, type),
    ...parsedAdditionalData
  };
  
  return componentInstance;
};

const stickyBlockBuilder = {
    name: 'stickyBlockBuilder',
    component(settings, colorSchema = '', type = '') {
      // Transparent header logic helper
      const transparentHeader = {
        isActive: false,
        isInverted: false,
        invertOnScroll: false,
        invertEnabled: false,
        hasInverted: false,
        stickyMode: 'never', // 'never', 'always', 'scroll-up'
        lastScrollY: 0,
        isSticky: false,
        handleScroll: null,
        
        init() {
          if (!settings.custom_id) return;
          // Invert logic driven by settings
          this.invertEnabled = !!settings.enable_invert_header;
          this.invertOnScroll = !!settings.invert_on_scroll;
          this.isInverted = this.invertEnabled && !this.invertOnScroll;
          this.hasInverted = false;
          
          if (settings.custom_id.startsWith('transparent-header-on')) {
            this.isActive = true;
            if (settings.header_sticky_mode) {
              this.stickyMode = settings.header_sticky_mode;
            } else if (settings.custom_id.includes('sticky')) {
              this.stickyMode = 'always';
            } else if (settings.custom_id.includes('scroll-up')) {
              this.stickyMode = 'scroll-up';
            }
          }
        },
        
        applyStyles(el) {
          if (!this.isActive || !el) return;
          el.classList.add('transparent-header');
          
          // Only apply invert if enabled and not on-scroll
          if (this.invertEnabled && !this.invertOnScroll) {
            el.classList.add('transparent-header--inverted');
            this.hasInverted = true;
          } else {
            el.classList.remove('transparent-header--inverted');
            this.hasInverted = false;
          }
          
          el.style.background = 'transparent';
          el.style.transition = 'background 0.3s, color 0.3s, filter 0.3s';
        },
        
        updateSticky(el) {
          if (!this.isActive || !el) return;
          
          if (this.stickyMode === 'never') {
            el.classList.remove('is-sticky');
            el.style.position = '';
            return;
          }
          
          if (this.stickyMode === 'always') {
            el.classList.add('is-sticky');
            el.style.position = 'absolute';
            el.style.top = '0';
            return;
          }
          
          // scroll-up sticky
          if (this.stickyMode === 'scroll-up') {
            const scrollY = window.scrollY;
            if (scrollY < this.lastScrollY && scrollY > 50) {
              // Scrolling up
              el.classList.add('is-sticky');
              el.style.position = 'sticky';
              el.style.top = '0';
            } else {
              el.classList.remove('is-sticky');
              el.style.position = '';
            }
            this.lastScrollY = scrollY;
          }
          
          // Handle invert on scroll
          if (this.invertEnabled && this.invertOnScroll) {
            if (window.scrollY > 50 && !this.hasInverted) {
              el.classList.add('transparent-header--inverted');
              el.style.filter = 'invert(1)';
              this.hasInverted = true;
            } else if (window.scrollY <= 50 && this.hasInverted) {
              el.classList.remove('transparent-header--inverted');
              el.style.filter = '';
              this.hasInverted = false;
            }
          }
        },
        
        bindEvents(el) {
          if (!this.isActive || !el) return;
          if (this.stickyMode === 'scroll-up' || this.invertOnScroll) {
            this.handleScroll = () => this.updateSticky(el);
            window.addEventListener('scroll', this.handleScroll);
          }
        },
        
        unbindEvents() {
          if (this.handleScroll) {
            window.removeEventListener('scroll', this.handleScroll);
            this.handleScroll = null;
          }
        }
      };

      // Utility: Re-parent block-builder to a new parent on load
      function reparentBlockBuilder(settings) {
        if (!settings || !settings.reparent_to_selector) return;

        const blockEl = document.querySelector(`.${settings.custom_column_class}`);
        const newParent = document.querySelector(settings.reparent_to_selector);
        if (!newParent || !blockEl) return;

        // Insert at specific child index or append
        if (typeof settings.reparent_child_index === 'number') {
          const children = Array.from(newParent.children);
          if (settings.reparent_child_index >= 0 && settings.reparent_child_index < children.length) {
            newParent.insertBefore(blockEl, children[settings.reparent_child_index]);
          } else {
            newParent.appendChild(blockEl);
          }
        } else {
          newParent.appendChild(blockEl);
        }
      }

      return {
        isVisible: settings.interaction_controlled ? (settings.default_visible === true) : true,
        targetEventIds: settings.target_event_ids ? settings.target_event_ids.split(',').map(item => item.trim()) : [],
        targetEventId: settings.target_event_id || '',
        hasSticky: settings.always_show_bg || false,
        eventListeners: [],
        classes: '', // CSS classes for animations and styling

        init() {
          // Transparent header logic: initialize, apply styles, and bind events
          const isHome = window.location.pathname === '/' || window.location.pathname === '/index.html';
          const isCollection = window.location.pathname.startsWith('/collections/') && !window.location.pathname.includes('/products/');
          const isBlog = window.location.pathname.startsWith('/blogs/');
          const isPage = window.location.pathname.startsWith('/pages/');
          const isProduct = window.location.pathname.startsWith('/products/');

          const allowTransparent =
            (settings.transparent_header_home_only && isHome) ||
            (settings.transparent_header_collection_only && isCollection) ||
            (settings.transparent_header_blog_only && isBlog) ||
            (settings.transparent_header_page_only && isPage) ||
            (settings.transparent_header_product_only && isProduct);

          // If no specific page settings, allow everywhere
          const noPageRestrictions = !settings.transparent_header_home_only && 
                                   !settings.transparent_header_collection_only && 
                                   !settings.transparent_header_blog_only && 
                                   !settings.transparent_header_page_only && 
                                   !settings.transparent_header_product_only;

          if (noPageRestrictions || allowTransparent) {
            transparentHeader.init();
          }

          // Wait for Alpine to mount the element, then apply styles and events
          setTimeout(() => {
            if (this.$el) {
              if (noPageRestrictions || allowTransparent) {
                if (window.enableDebugMode) { console.log('Applying transparent header styles and events'); }
                transparentHeader.applyStyles(this.$el);
                transparentHeader.updateSticky(this.$el);
                transparentHeader.bindEvents(this.$el);
              }
              
              // Re-parent logic
              reparentBlockBuilder(settings);
            }
          }, 0);

          // Set up event listeners for targetEventIds
          this.setupEventListeners();

          // Add scroll event listener for background changes
          if (settings.apply_bg_on_scroll) {
            const scrollThreshold = settings.scroll_threshold || 50;
            window.addEventListener('scroll', () => {
              const scrollY = window.scrollY;
              if (scrollY >= scrollThreshold) {
                this.hasSticky = true;
              } else {
                this.hasSticky = settings.always_show_bg || false;
              }
            });

            // Check initial scroll position
            if (window.scrollY >= (settings.scroll_threshold || 50)) {
              this.hasSticky = true;
            }
          }
        },

        setupEventListeners() {
          // Handle single target event ID
          if (settings.target_event_id) {
            this.addEventListenerForId(settings.target_event_id);
          }

          // Handle multiple target event IDs
          if (this.targetEventIds && this.targetEventIds.length > 0) {
            this.targetEventIds.forEach(eventId => {
              if (eventId && eventId !== settings.target_event_id) {
                this.addEventListenerForId(eventId);
              }
            });
          }
        },

        addEventListenerForId(eventId) {
          const eventListener = (event) => {
            // Prevent infinite recursion by checking if this event was dispatched by this component
            if (event.detail && event.detail.sourceBlockId === this.$el?.dataset?.blockId) {
              return;
            }
            
            if (window.enableDebugMode) { console.log('Target event triggered:', eventId); }
            this.handleEventAction(eventId, event);
          };

          window.addEventListener(eventId, eventListener);
          this.eventListeners.push({ eventId, listener: eventListener });
        },

        handleEventAction(eventId, event) {
          // Dispatch custom event for this specific eventId with source identification
          const customEvent = new CustomEvent(eventId, {
            detail: {
              originalEvent: event,
              blockId: this.$el?.dataset?.blockId,
              sectionId: settings.custom_id,
              eventId: eventId,
              sourceBlockId: this.$el?.dataset?.blockId // Add source identification
            }
          });
          
          window.dispatchEvent(customEvent);

          // Handle interaction behavior if enabled
          if (settings.interaction_controlled) {
            this.applyInteractionBehavior(settings.interaction_behavior || 'toggle');
          }
        },

        handleInteractionEvent(event) {
          if (!settings.interaction_controlled) return;

          // Check if this event is meant for this block builder instance
          if (!this.isTargetEvent(event)) return;

          if (window.enableDebugMode) { console.log('Sticky block builder handling interaction event:', event.type, event.detail); }

          // Use action system for show/hide instead of direct manipulation
          if (settings.enable_actions) {
            this.triggerVisibilityAction(event);
          } else {
            // Fallback to direct toggle
            this.applyInteractionBehavior(settings.interaction_behavior || 'toggle');
          }
        },

        isTargetEvent(event) {
          // If no targeting is configured, don't handle any interaction events
          if (!settings.target_menu_id && !settings.target_menu_title && !settings.target_menu_handles) {
            return false;
          }

          const eventDetail = event.detail || {};
          const linkData = eventDetail.linkData || {};
          const eventId = eventDetail.eventId || '';

          // Check if the event ID matches our target event ID
          if (settings.target_event_id && eventId === settings.target_event_id) {
            return true;
          }

          // Check if the link title matches our target menu title
          if (settings.target_menu_title && linkData.title === settings.target_menu_title) {
            return true;
          }

          // Check if the link handle/id matches our target menu id
          if (settings.target_menu_id && (linkData.handle === settings.target_menu_id || linkData.title === settings.target_menu_id)) {
            return true;
          }

          // Check if the link handle is in our target menu handles list
          if (settings.target_menu_handles) {
            const targetHandles = settings.target_menu_handles.split(',').map(h => h.trim());
            if (linkData.handle && targetHandles.includes(linkData.handle)) {
              return true;
            }
            if (linkData.title && targetHandles.includes(linkData.title)) {
              return true;
            }
          }

          return false;
        },

        triggerVisibilityAction(event) {
          const actionData = {
            blockId: this.$el?.dataset?.blockId,
            sectionId: settings.custom_id,
            action: this.getActionFromBehavior(settings.interaction_behavior || 'toggle'),
            method: 'display',
            transition: '0.3s ease',
            fadeAnimation: true
          };

          // Trigger action via the action system
          if (typeof window.triggerAction === 'function') {
            window.triggerAction('runBlockVisibility', actionData);
          } else {
            // Fallback to direct event dispatch
            window.dispatchEvent(new CustomEvent('arc:action:blockVisibility', {
              detail: actionData
            }));
          }
        },

        getActionFromBehavior(behavior) {
          switch (behavior) {
            case 'show-only': return 'show';
            case 'hide-only': return 'hide';
            case 'toggle':
            default: return 'toggle';
          }
        },

        applyInteractionBehavior(behavior) {
          switch (behavior) {
            case 'toggle':
              this.isVisible = !this.isVisible;
              break;
            case 'show-only':
              this.isVisible = true;
              break;
            case 'hide-only':
              this.isVisible = false;
              break;
            default:
              this.isVisible = !this.isVisible;
              break;
          }
        },

        handleSectionLeave() {
          if (!settings.interaction_controlled) return;
          
          if (!settings.stay_visible_after_interaction) {
            this.isVisible = false;
          }
        },

        // Action handling methods (same as blockBuilder)
        handleGroupActions(event) {
          this.handleActions(event, 'group');
        },

        handleSectionActions(event) {
          this.handleActions(event, 'section');
        },

        handleActions(event, type) {
          if (!settings.enable_actions) return;

          event.preventDefault();
          event.stopPropagation();

          const actionsToRun = this.getActionsToRun();
          if (actionsToRun.length === 0) return;

          const actionData = this.buildActionData(type);
          this.executeActions(actionsToRun, actionData);
        },

        getActionsToRun() {
          if (settings.action_preset && settings.action_preset !== '') {
            return [settings.action_preset];
          }
          
          if (settings.actions) {
            return settings.actions.split(',').map(action => action.trim()).filter(Boolean);
          }
          
          return [];
        },

        buildActionData(type) {
          let actionData = {};
          
          if (settings.action_data) {
            try {
              actionData = JSON.parse(settings.action_data);
            } catch (e) {
              console.warn('Invalid action data JSON:', settings.action_data);
            }
          }

          // Add target element data
          if (settings.target_element_id) actionData.elementId = settings.target_element_id;
          if (settings.target_element_selector) actionData.selector = settings.target_element_selector;
          if (settings.target_block_id) actionData.blockId = settings.target_block_id;
          if (settings.target_section_id) actionData.sectionId = settings.target_section_id;

          // Add source element reference
          actionData.sourceElement = this.$el;
          actionData.sourceBlockId = this.$el?.dataset?.blockId;

          return actionData;
        },

        executeActions(actionsToRun, actionData) {
          const delay = settings.action_delay || 0;

          try {
            if (typeof window.triggerAction === 'function') {
              if (actionsToRun.length === 1) {
                window.triggerAction(actionsToRun[0], actionData);
              } else {
                window.triggerActionChain(actionsToRun, actionData, delay);
              }
            } else {
              // Fallback to direct event dispatch
              this.fallbackExecuteActions(actionsToRun, actionData, delay);
            }
          } catch (error) {
            console.error('Error executing actions:', error);
          }
        },

        fallbackExecuteActions(actions, data, delay) {
          actions.forEach((actionName, index) => {
            setTimeout(() => {
              const eventName = `arc:action:${actionName}`;
              window.dispatchEvent(new CustomEvent(eventName, { detail: data }));
            }, index * delay);
          });
        },

        // Cleanup
        destroy() {
          if (this.eventListeners && this.eventListeners.length > 0) {
            this.eventListeners.forEach(({ eventId, listener }) => {
              window.removeEventListener(eventId, listener);
            });
            this.eventListeners = [];
          }
          transparentHeader.unbindEvents();
        }
      }
    }
  };

// Register this component with the utility system when available
if (typeof window.createAlpineComponent === 'function') {
  window.createAlpineComponent('stickyBlockBuilder', stickyBlockBuilder.component);
} else {
  // If utility system isn't loaded yet, register when it becomes available
  document.addEventListener('DOMContentLoaded', () => {
    if (typeof window.createAlpineComponent === 'function') {
      window.createAlpineComponent('stickyBlockBuilder', stickyBlockBuilder.component);
    }
  });
}

export default stickyBlockBuilder;
